import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, RefreshCw, Sparkles, Bomb as BombIcon, CircleOff, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getLobeIcon } from '@/lib/lobe-icon'
import { formatQuotaWithCurrency } from '@/lib/currency'
import { useSlotMachineStatus, useSlotMachineSpin } from '../hooks/use-slot-machine'
import { SYMBOL_MAP } from '../types'
import type { SpinResult } from '../types'
import { SlotMachineHistory } from './slot-machine-history'

interface SlotMachineGameProps {
  onBack: () => void
  status: NonNullable<ReturnType<typeof useSlotMachineStatus>['data']>
}

function SymbolDisplay({ symbolId, size = 'md', animated = false, highlighted = false }: { symbolId: string; size?: 'sm' | 'md' | 'lg'; animated?: boolean; highlighted?: boolean }) {
  const symbol = SYMBOL_MAP[symbolId]

  const sizeClasses = size === 'sm' ? 'w-10 h-10 text-lg' : size === 'lg' ? 'w-[76px] h-[76px]' : 'w-16 h-16'

  const isBomb = symbolId === 'bomb'

  return (
    <div
      className={`${sizeClasses} rounded-xl flex flex-col items-center justify-center font-bold select-none transition-all duration-150 ${animated ? 'animate-pulse' : ''} ${highlighted ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-background scale-105 shadow-lg' : ''}`}
      style={{ backgroundColor: `${symbol?.color || '#666'}20`, border: `2px solid ${symbol?.color || '#666'}40` }}
    >
      {isBomb ? (
        <BombIcon className={size === 'sm' ? 'h-5 w-5' : size === 'lg' ? 'h-8 w-8' : 'h-6 w-6'} style={{ color: symbol?.color }} />
      ) : symbol?.lobeIcon ? (
        <div className='flex items-center justify-center'>
          {getLobeIcon(symbol.lobeIcon, size === 'lg' ? 36 : size === 'sm' ? 18 : 24)}
        </div>
      ) : (
        <span className='text-lg font-bold' style={{ color: symbol?.color }}>
          {symbol?.label?.charAt(0) || '?'}
        </span>
      )}
      {size !== 'sm' && symbol && (
        <span className='text-[10px] mt-0.5 opacity-70' style={{ color: symbol.color }}>
          {symbol.label}
        </span>
      )}
    </div>
  )
}

function ReelColumn({ symbols, finalSymbol, delay, spinning, onComplete }: {
  symbols: string[]
  finalSymbol: string
  delay: number
  spinning: boolean
  onComplete: () => void
}) {
  const [displaySymbols, setDisplaySymbols] = useState(symbols)
  const [stopped, setStopped] = useState(false)

  useEffect(() => {
    if (!spinning) {
      setStopped(false)
      setDisplaySymbols(symbols)
      return
    }

    setStopped(false)
    const allSymbols = ['gpt', 'claude', 'gemini', 'deepseek', 'qwen', 'spark', 'glm', 'yi', 'doubao', 'bomb']
    const interval = setInterval(() => {
      setDisplaySymbols([
        allSymbols[Math.floor(Math.random() * allSymbols.length)],
        allSymbols[Math.floor(Math.random() * allSymbols.length)],
        allSymbols[Math.floor(Math.random() * allSymbols.length)],
      ])
    }, 80)

    const stopTimeout = setTimeout(() => {
      clearInterval(interval)
      const allSymbols = ['gpt', 'claude', 'gemini', 'deepseek', 'qwen', 'spark', 'glm', 'yi', 'doubao', 'bomb']
      const otherSymbols = allSymbols.filter((s) => s !== finalSymbol)
      const getRandomOther = () => otherSymbols[Math.floor(Math.random() * otherSymbols.length)]
      setDisplaySymbols([getRandomOther(), finalSymbol, getRandomOther()])
      setStopped(true)
      onComplete()
    }, 1000 + delay)

    return () => {
      clearInterval(interval)
      clearTimeout(stopTimeout)
    }
  }, [spinning])

  return (
    <div className='flex flex-col gap-1.5'>
      {displaySymbols.map((sym, i) => (
        <SymbolDisplay
          key={`${sym}-${i}-${stopped ? 'stopped' : 'spinning'}`}
          symbolId={sym}
          size='lg'
          animated={spinning && !stopped}
          highlighted={stopped && i === 1}
        />
      ))}
    </div>
  )
}

export function SlotMachineGame({ onBack, status }: SlotMachineGameProps) {
  const { t } = useTranslation()
  const spinMutation = useSlotMachineSpin()
  const [spinning, setSpinning] = useState(false)
  const [result, setResult] = useState<SpinResult | null>(null)
  const [completedColumns, setCompletedColumns] = useState(0)
  const [showHistory, setShowHistory] = useState(false)

  const handleSpin = useCallback(async () => {
    setResult(null)
    setCompletedColumns(0)
    setSpinning(true)

    const res = await spinMutation.mutateAsync()
    if (res?.data) {
      setResult(res.data)
    } else {
      setSpinning(false)
    }
  }, [spinMutation])

  const handleColumnComplete = useCallback(() => {
    setCompletedColumns((prev) => prev + 1)
  }, [])

  useEffect(() => {
    if (completedColumns >= 3) {
      const timeout = setTimeout(() => {
        setSpinning(false)
      }, 300)
      return () => clearTimeout(timeout)
    }
  }, [completedColumns])

  const defaultSymbols = ['gpt', 'claude', 'gemini']

  return (
    <div className='mx-auto max-w-lg space-y-6'>
      <div className='flex items-center gap-2'>
        <Button variant='ghost' size='icon' onClick={onBack}>
          <ArrowLeft className='h-4 w-4' />
        </Button>
        <div>
          <h2 className='text-lg font-semibold'>{t('Slot Machine')}</h2>
          <p className='text-muted-foreground text-xs'>{t('Try your luck and win quota rewards')}</p>
        </div>
      </div>

      <Card className='overflow-hidden'>
        <div className='bg-gradient-to-br from-purple-600/10 via-pink-500/5 to-indigo-600/10 p-6'>
          <div className='flex items-center justify-center gap-3 mb-4'>
            <ReelColumn
              symbols={spinning ? [defaultSymbols[0], defaultSymbols[1], defaultSymbols[2]] : defaultSymbols}
              finalSymbol={result?.symbols?.[0] || defaultSymbols[0]}
              delay={0}
              spinning={spinning}
              onComplete={handleColumnComplete}
            />
            <ReelColumn
              symbols={spinning ? [defaultSymbols[0], defaultSymbols[1], defaultSymbols[2]] : defaultSymbols}
              finalSymbol={result?.symbols?.[1] || defaultSymbols[1]}
              delay={200}
              spinning={spinning}
              onComplete={handleColumnComplete}
            />
            <ReelColumn
              symbols={spinning ? [defaultSymbols[0], defaultSymbols[1], defaultSymbols[2]] : defaultSymbols}
              finalSymbol={result?.symbols?.[2] || defaultSymbols[2]}
              delay={400}
              spinning={spinning}
              onComplete={handleColumnComplete}
            />
          </div>

          <div className='text-center'>
            {!spinning && result && (
              <div className='mb-4 animate-in fade-in slide-in-from-top-2 duration-300'>
                {result.result === 'win' && (
                  <div className='flex items-center justify-center gap-2 text-emerald-500'>
                    <Sparkles className='h-5 w-5' />
                    <span className='font-bold text-lg'>+{formatQuotaWithCurrency(result.reward_quota)}</span>
                  </div>
                )}
                {result.result === 'bomb' && (
                  <div className='flex items-center justify-center gap-2 text-red-500'>
                    <BombIcon className='h-5 w-5' />
                    <span className='font-bold text-lg'>-{formatQuotaWithCurrency(Math.abs(result.reward_quota))}</span>
                  </div>
                )}
                {result.result === 'loss' && (
                  <div className='flex items-center justify-center gap-2 text-muted-foreground'>
                    <CircleOff className='h-5 w-5' />
                    <span className='text-sm'>{t('No luck this time')}</span>
                  </div>
                )}
              </div>
            )}

            <Button
              size='lg'
              onClick={handleSpin}
              disabled={spinning || spinMutation.isPending || status.spins_remaining <= 0}
              className='min-w-[180px]'
            >
              {spinMutation.isPending ? (
                <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
              ) : spinning ? (
                <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
              ) : (
                <Play className='mr-2 h-4 w-4' />
              )}
              {status.spins_remaining <= 0 && !spinning
                ? t('No spins left')
                : spinning
                ? t('Spinning...')
                : t('Spin')}
            </Button>
          </div>
        </div>
      </Card>

      <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
        <Card>
          <CardHeader className='p-3 pb-1'>
            <CardTitle className='text-xs text-muted-foreground'>{t('Spins Today')}</CardTitle>
          </CardHeader>
          <CardContent className='p-3 pt-0'>
            <span className='text-xl font-bold'>{status.spins_today}/{status.max_spins_per_day}</span>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='p-3 pb-1'>
            <CardTitle className='text-xs text-muted-foreground'>{t('Total Wins')}</CardTitle>
          </CardHeader>
          <CardContent className='p-3 pt-0'>
            <span className='text-xl font-bold text-emerald-500'>{status.total_wins}</span>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='p-3 pb-1'>
            <CardTitle className='text-xs text-muted-foreground'>{t('Total Reward')}</CardTitle>
          </CardHeader>
          <CardContent className='p-3 pt-0'>
            <span className='text-xl font-bold text-emerald-500'>{formatQuotaWithCurrency(status.total_reward)}</span>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='p-3 pb-1'>
            <CardTitle className='text-xs text-muted-foreground'>{t('Cost per Spin')}</CardTitle>
          </CardHeader>
          <CardContent className='p-3 pt-0'>
            <span className='text-xl font-bold'>{formatQuotaWithCurrency(status.cost_per_spin)}</span>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className='p-4 pb-2 flex flex-row items-center justify-between'>
          <CardTitle className='text-sm'>{t('Game History')}</CardTitle>
          <Button variant='ghost' size='sm' onClick={() => setShowHistory(!showHistory)}>
            {showHistory ? t('Hide') : t('Show')}
          </Button>
        </CardHeader>
        {showHistory && (
          <CardContent className='p-4 pt-2'>
            <SlotMachineHistory />
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader className='p-4 pb-2'>
          <CardTitle className='text-sm'>{t('Game Rules')}</CardTitle>
        </CardHeader>
        <CardContent className='p-4 pt-2 space-y-1.5 text-sm text-muted-foreground'>
          <p>{t('Each spin costs {{cost}}', { cost: formatQuotaWithCurrency(status.cost_per_spin) })}</p>
          <p>{t('Match 2 symbols: Win random reward')}</p>
          <p>{t('Match 3 symbols: Win bigger reward')}</p>
          <p>{t('Bomb symbol: Lose quota')}</p>
          <p>{t('Max {{max}} spins per day', { max: status.max_spins_per_day })}</p>
        </CardContent>
      </Card>
    </div>
  )
}
